import { sentimentColorMap, TOTAL_METRIC_GROUPS } from '@/app/dashboards/constants';
import { ALL_CHANNELS_KEY } from '@/models/dashboards/channels.enum';
import { useReport } from '@/app/dashboards/composables/useReport';
import { computed } from 'vue';
import Formatters from '@dashhudson/dashing-ui/utils/formatters';
import { getChartColor } from '@dashhudson/dashing-ui/utils/charts';
import * as TOKENS from '@dashhudson/dashing-ui/tokens';
import { formatGoal } from '@/app/dashboards/utils/report.utils';
import { DISPLAY_STRATEGY } from '@/app/dashboards/reportConstants';
import isNil from 'lodash/isNil';
import { LISTENING_REPORT_AGGREGATE_BY_TYPES } from '@/models/dashboards/aggregate-type.enum';
import { useReportMetaAggregateBy } from '@/app/dashboards/composables/useReportMetaAggregateBy';

export function useHorizontalBarReportDetails({ rawGraphReportData, report }) {
  const {
    aggregateBy,
    displayStrategy,
    secondaryAggregateBy,
    firstMetric,
    showGoalChip,
    reportMeta,
  } = useReport({
    report,
  });

  const { disableAggregationSort } = useReportMetaAggregateBy({ aggregateBy });
  const singleBarMeters = computed(() =>
    Object.entries(rawGraphReportData.value).reduce((acc, [key, group]) => {
      const channelKey = aggregateBy.value !== TOTAL_METRIC_GROUPS.CHANNEL ? ALL_CHANNELS_KEY : key;
      const rawMetricData = group.metrics?.[firstMetric.value]?.[channelKey]?.value;
      if (key !== 'metrics') {
        // Skip total metric
        const meter = {
          label: group.name ?? key,
          data: [{ value: rawMetricData }],
        };

        if (displayStrategy.value === DISPLAY_STRATEGY.PERCENT_OF_GRAND_TOTAL) {
          meter.customValue = Formatters.formatPercentage(rawMetricData);
        }
        if (isNil(rawMetricData)) {
          meter.data = [{ value: 0 }]; // Horizontal Bar chart can't handle null/undefined values. Set to 0
          meter.customValue = '-';
        }
        if (aggregateBy.value === LISTENING_REPORT_AGGREGATE_BY_TYPES.SENTIMENT.value) {
          meter.data[0].color = sentimentColorMap[key];
        }
        acc.push(meter);
      }

      return acc;
    }, []),
  );

  const stackedBarMeters = computed(() => {
    const allPrimaryBreakdownEntries = Object.entries(rawGraphReportData.value ?? {}).filter(
      ([key, value]) => {
        if (key === 'metrics') return false;
        return !(aggregateBy.value && value.data_type !== aggregateBy.value);
      },
    );

    return allPrimaryBreakdownEntries.map(([primaryBreakdownKey, primaryBreakdownData]) => {
      const primaryLabel = primaryBreakdownData?.name;
      const secondaryValues = Object.keys(primaryBreakdownData?.breakdowns || {});
      const data = secondaryValues.map((secondaryKey, index) => {
        const secondaryLabel = primaryBreakdownData.breakdowns?.[secondaryKey]?.name;
        const channelKey =
          secondaryAggregateBy.value !== TOTAL_METRIC_GROUPS.CHANNEL
            ? ALL_CHANNELS_KEY
            : secondaryKey;
        let secondaryValue =
          primaryBreakdownData.breakdowns?.[secondaryKey]?.metrics?.[firstMetric.value]?.[
            channelKey
          ]?.value;
        // Horizontal Bar chart can't handle null/undefined values. Set to 0
        if (isNil(secondaryValue)) secondaryValue = 0;
        const chartColor =
          secondaryAggregateBy.value === LISTENING_REPORT_AGGREGATE_BY_TYPES.SENTIMENT.value
            ? sentimentColorMap[secondaryKey]
            : getChartColor(index);
        return {
          label: secondaryLabel,
          value: secondaryValue,
          color: chartColor,
        };
      });
      const meter = {
        label: primaryLabel,
        data,
      };
      if (displayStrategy.value === DISPLAY_STRATEGY.PERCENT_OF_GRAND_TOTAL) {
        const primaryChannelKey =
          aggregateBy.value !== TOTAL_METRIC_GROUPS.CHANNEL
            ? ALL_CHANNELS_KEY
            : primaryBreakdownKey;
        const percentOfTotal =
          primaryBreakdownData.metrics?.[firstMetric.value]?.[primaryChannelKey]?.value;
        meter.customValue = Formatters.formatPercentage(percentOfTotal);
      }
      return meter;
    });
  });

  const goalMarker = computed(() => {
    const { label, value } = formatGoal(
      reportMeta.value.goals?.overall?.type,
      reportMeta.value.goals?.overall?.value,
    );
    return [
      {
        value,
        label,
        color: TOKENS.COLOR_NEUTRAL_800,
        legend: {
          label: 'Set Goal',
          value: 'goal-marker',
        },
      },
    ];
  });

  const isStacked = computed(() => Boolean(secondaryAggregateBy.value));

  const horizontalBarChartData = computed(() => {
    const data = { meters: [] };
    data.meters = isStacked.value ? stackedBarMeters.value : singleBarMeters.value;
    if (showGoalChip.value) {
      data.markers = goalMarker.value;
    }
    // Disable dashing-ui value based sorting and sort by object keys
    if (disableAggregationSort.value) {
      data.disableSorting = true;
    }
    return data;
  });

  const maxBarLength = computed(() => {
    let valueList = [0];
    if (isStacked.value) {
      valueList = horizontalBarChartData.value?.meters.map((meter) =>
        meter?.data.reduce((acc, datapoint) => acc + (datapoint?.value ?? 0), 0),
      );
    } else {
      valueList = horizontalBarChartData.value?.meters?.map((meter) => meter.data[0].value) || [0];
    }
    return Math.max(...valueList);
  });

  const horizontalBarChartOptions = computed(() => ({
    min: 0,
    max: maxBarLength.value,
  }));

  const showHorizontalLegend = computed(() => {
    // We count secondary breakdown options because that is what is displayed as checkboxes in chart legend
    if (!isStacked.value) return false;

    const primaryBreakdowns = Object.entries(rawGraphReportData.value ?? {}).filter(
      ([key, value]) =>
        key !== 'metrics' && (!aggregateBy.value || value.data_type === aggregateBy.value),
    );

    const secondaryKeys = new Set(
      primaryBreakdowns.flatMap(([, data]) => Object.keys(data?.breakdowns || {})),
    );

    return secondaryKeys.size > 0 && secondaryKeys.size < 12;
  });

  return {
    horizontalBarChartData,
    horizontalBarChartOptions,
    showHorizontalLegend,
  };
}
